import { releaseNameFor } from '@utils/product';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import AvantArteAuthGateway from '../gateways/AvantArteAuthGateway';
import { AvantArteProductGateway } from '../gateways/AvantArteProductGateway';
import { ShopifyCheckoutGateway } from '../gateways/ShopifyCheckoutGateway';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import { useProductPurchasing } from '../hooks/useProductPurchasing';
import { useReleaseGating } from '../hooks/useReleaseGating';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../types/Purchasing';
import { ProductSourceBackend } from './ProductProvider';

const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const productsGateway = new ShopifyProductsGateway(storefrontClient);

type PurchaseContextType =
  ProductPurchasingController<ProductPurchaseDetails> & {
    backendProduct: ProductSourceBackend | null;
  };

interface PurchaseContextProps {
  backendProduct?: ProductSourceBackend | null;
  children: ReactNode;
  product: ProductType | null;
  productDetails: ProductPurchaseDetails | null;
}

const PurchaseContext = createContext<PurchaseContextType>({
  backendProduct: null,
  loading: true,
  checkoutLoading: false,
  dispatchDate: { isPast: false, range: null, weeks: null },
  product: null,
  frame: null,
  frameOptions: null,
  unlockCode: undefined,
  quantity: 1,
  release: undefined,
  releaseGating: undefined,
  requiresGating: false,
  canPurchase: true,
  setFrame: () => {},
  goToCheckout: () => Promise.resolve(),
  setQuantity: () => {},
  setUnlockCode: () => {},
  collectionSetToggleId: () => {},
  collectionSetToggleAll: () => {},
  collectionSetUpdateQuantity: () => {},
  collectionSet: null,
  collectionSetDescription: null,
  collectionSetImage: null,
  isFramedByDefault: false,
  amlCheckNeeded: false,
  isPreSelectedCollectionSet: false,
  setIsPreSelectedCollectionSet: () => {},
});

export const PurchaseProvider = ({
  backendProduct = null,
  children,
  product,
  productDetails,
}: PurchaseContextProps) => {
  const identifier = product?.id?.[0] as ShopifyProductIdentifier;
  const release = releaseNameFor(product || undefined);
  const releaseGating = useReleaseGating({ product: product || undefined });
  const releaseType = product?.releaseType;

  const state = useProductPurchasing({
    identifier,
    release,
    releaseGating,
    releaseType,
    productDetails,
    authGateway,
    aaProductsGateway,
    checkoutGateway,
    productsGateway,
  });

  const context = useMemo(
    () => ({ ...state, backendProduct }),
    [backendProduct, state],
  );

  return (
    <PurchaseContext.Provider value={context}>
      {children}
    </PurchaseContext.Provider>
  );
};

export const usePurchase = () => useContext(PurchaseContext);
